import { Typography } from "@mui/material";
import type { FC, PropsWithChildren } from "react";
import { useEffect, useRef } from "react";
import InfiniteScrollComponent from "react-infinite-scroll-component";
import "./InfiniteScroll.scss";

interface Props {
  dataLength: number;
  fetchData: () => void;
  hasMore: boolean;
  scrollableTarget?: string | null;
  maxHeight?: number;
}

const InfiniteScroll: FC<PropsWithChildren<Props>> = ({
  children,
  dataLength,
  hasMore,
  fetchData,
  scrollableTarget = null,
  maxHeight,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // react-infinite-scroll-component only requests the next page from a scroll event, so when the rendered
  // items do not fill the scroll container there is nothing to scroll and the list never grows past the first page.
  useEffect(() => {
    const loadMoreIfNotScrollable = () => {
      const scrollContainer = scrollableTarget == null ? document.documentElement : scrollContainerRef.current;
      const visibleHeight = scrollableTarget == null ? window.innerHeight : scrollContainer?.clientHeight;
      if (hasMore && scrollContainer && visibleHeight !== undefined && scrollContainer.scrollHeight <= visibleHeight) {
        fetchData();
      }
    };
    loadMoreIfNotScrollable();
    window.addEventListener("resize", loadMoreIfNotScrollable);
    return () => window.removeEventListener("resize", loadMoreIfNotScrollable);
  }, [dataLength, hasMore, fetchData, scrollableTarget]);

  return (
    <div
      ref={scrollContainerRef}
      style={{ flexGrow: 1, overflow: "auto", maxHeight: maxHeight }}
      id={scrollableTarget == null ? undefined : scrollableTarget}
    >
      <InfiniteScrollComponent
        dataLength={dataLength}
        next={fetchData}
        hasMore={hasMore}
        loader={
          <Typography
            variant="h5"
            sx={{
              marginTop: 1,
            }}
          >
            Loading...
          </Typography>
        }
        scrollableTarget={scrollableTarget == null ? undefined : scrollableTarget}
      >
        {children}
      </InfiniteScrollComponent>
    </div>
  );
};

export default InfiniteScroll;
